<template>
    <div class="ppv2-p-8">
        <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-8">
            <!-- Name -->
            <BaseInput
                v-model="form.name"
                :label="$t('productSupplier.createPopup.nameLabel')"
                :placeholder="$t('productSupplier.createPopup.namePlaceholder')"
                required
                :customSizeLabel="16"
                customClassLabel="poppinsSemiBold ppv2-text-[#1A1F37]"
                :error="errors.name ? errors.name[0] : ''"
            />
            <!-- Email -->
            <BaseInput
                v-model="form.email"
                :label="$t('productSupplier.createPopup.emailLabel')"
                :placeholder="$t('productSupplier.createPopup.emailPlaceholder')"
                :customSizeLabel="16"
                customClassLabel="poppinsSemiBold ppv2-text-[#1A1F37]"
                :error="errors.email ? errors.email[0] : ''"
            />
            <!-- Phone -->
            <BaseInput
                v-model="form.phone_number"
                :label="$t('productSupplier.createPopup.phoneLabel')"
                :placeholder="$t('productSupplier.createPopup.phonePlaceholder')"
                :customSizeLabel="16"
                customClassLabel="poppinsSemiBold ppv2-text-[#1A1F37]"
                :error="errors.phone_number ? errors.phone_number[0] : ''"
            />
            <!-- Brands -->
            <BaseSelect
                v-model="form.brands"
                label="Marques du fournisseur"
                placeholder="Associez une ou plusieurs marques"
                :options="brandsOptions"
                optionLabel="name"
                optionValue="id"
                :multiple="true"
                :customSizeLabel="16"
                customLabelClass="poppinsSemiBold ppv2-text-[#1A1F37]"
            />

            <!-- Reference -->
            <BaseInput
                v-model="form.ref"
                :label="$t('productSupplier.createPopup.refLabel')"
                :placeholder="$t('productSupplier.createPopup.refPlaceholder')"
                :customSizeLabel="16"
                customClassLabel="poppinsSemiBold ppv2-text-[#1A1F37]"
                :error="errors.ref ? errors.ref[0] : ''"
            />
            <!-- Address -->
            <BaseInput
                v-model="form.address"
                :label="$t('productSupplier.createPopup.addressLabel')"
                :placeholder="$t('productSupplier.createPopup.addressPlaceholder')"
                :customSizeLabel="16"
                customClassLabel="poppinsSemiBold ppv2-text-[#1A1F37]"
                :error="errors.address ? errors.address[0] : ''"
            />
        </div>

        <!-- Footer Actions -->
        <div class="ppv2-flex ppv2-justify-center ppv2-mt-10">
            <button
                @click="submit"
                :disabled="isLoading"
                class="ppv2-bg-[#34216D] ppv2-text-white ppv2-px-16 ppv2-py-3 ppv2-rounded-xl poppinsSemiBold hover:ppv2-bg-[#2a1a57] ppv2-transition-all disabled:ppv2-opacity-50 ppv2-shadow-lg"
                :style="{ fontSize: normalizeFontSize(16) }"
            >
                {{ $t('productSupplier.createPopup.validate') }}
            </button>
        </div>
    </div>
</template>

<script>
import BaseInput from '../../../Shared/BaseInput.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import axios from 'axios'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'SupplierForm',
    components: {
        BaseInput,
        BaseSelect,
    },
    props: {
        editItem: {
            type: Object,
            default: null,
        },
        token: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            isLoading: false,
            form: {
                name: '',
                email: '',
                phone_number: '',
                ref: '',
                address: '',
                brands: [],
            },
            brandsOptions: [],
            errors: {},
        }
    },
    computed: {
        isEdit() {
            return !!this.editItem
        },
    },
    created() {
        if (this.editItem) {
            this.form.name = this.editItem.name || ''
            this.form.email = this.editItem.email || ''
            this.form.phone_number = this.editItem.phone_number || ''
            this.form.ref = this.editItem.ref || ''
            this.form.address = this.editItem.address || ''
            this.form.brands = this.editItem.brands ? this.editItem.brands.map((b) => b.id) : []
        }
        this.fetchBrands()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        validateForm() {
            const errors = {}

            if (!this.form.name || this.form.name.trim() === '') {
                errors.name = [this.$t('productSupplier.validation.nameRequired')]
            }

            return errors
        },
        async submit() {
            this.errors = this.validateForm()

            if (Object.keys(this.errors).length > 0) {
                const firstError = Object.values(this.errors)[0][0]
                this.$toast.error(firstError)
                return
            }

            this.isLoading = true
            this.errors = {}

            try {
                let endpoint = ''
                let method = 'POST'

                if (this.isEdit) {
                    endpoint = `/api/v3/supplier/update/${this.editItem.id}`
                    method = 'PATCH'
                } else {
                    endpoint = '/api/v3/supplier/store'
                }

                const response = await axios({
                    method: method,
                    url: endpoint,
                    data: this.form,
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })

                if (response.data.success) {
                    this.$toast.success(this.isEdit ? 'Modifié avec succès' : 'Ajouté avec succès')
                    this.$emit('success', response.data.data)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                    const firstError = Object.values(this.errors)[0][0]
                    this.$toast.error(firstError)
                } else {
                    this.$toast.error('Une erreur est survenue')
                }
            } finally {
                this.isLoading = false
            }
        },
        async fetchBrands() {
            try {
                const response = await axios.get('/api/v3/supplier/fetch-brands', {
                    headers: {
                        Authorization: `Bearer ${this.token}`,
                    },
                })
                if (response.data.success) {
                    this.brandsOptions = response.data.data
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Error fetching brands:', error)
            }
        },
    },
}
</script>
